import React, { memo, useMemo } from 'react';
import { View } from 'react-native';
import { Circle, Line, Mask, Rect, Svg } from 'react-native-svg';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Squircle from 'design-system/Squircle';
import { Indicator, rem } from 'design-system';
import StatusIcon from 'design-system/StatusIcon';
import IconCircle from 'design-system/icons/IconCircle';
import IconTransfer from 'design-system/icons/IconTransfer';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { ReceiptRows, Row } from './ReceiptRows';
import { isWeb } from '../../../constants';
const BORDER_RADIUS = rem(24);
const PADDING = rem(16);
const MASK_VIEW_HEIGHT = rem(27);
const strings = {
    statusTitle: 'Status',
    status: {
        queued: 'Queued',
        pending: 'Pending',
        completed: 'Completed',
        cancelled: 'Cancelled',
        failed: 'Failed',
        partially_filled: 'Partially completed',
    },
};
const MaskView = React.memo(({ width }) => {
    const colors = useColors();
    const { width: WIDTH } = useAppFrameDimensions(isWeb);
    const ORDER_WIDTH = (width || WIDTH) - rem(32);
    return (<Svg width={ORDER_WIDTH + 2} height={MASK_VIEW_HEIGHT + 2} viewBox={`0 0 ${ORDER_WIDTH} ${MASK_VIEW_HEIGHT}`} fill="none" preserveAspectRatio="none">
      <Mask id="maskReceipt" fill="white">
        <Rect x="0" y="0" width={`${ORDER_WIDTH}`} height={`${MASK_VIEW_HEIGHT}`} fill="white"/>
        <Circle cx={`${rem(2)}`} cy={`${rem(13)}`} r={`${rem(13)}`} fill="black"/>
        <Circle cx={`${ORDER_WIDTH - rem(2)}`} cy={`${rem(13)}`} r={`${rem(13)}`} fill="black" strokeDasharray="2,4"/>
        <Line strokeWidth="2" stroke="black" x1={`${rem(13)}`} y1={`${rem(13)}`} y2={`${rem(13)}`} strokeDasharray="5" x2={`${ORDER_WIDTH - rem(13)}`}/>
      </Mask>
      <Rect x="0" y="0" width={`${ORDER_WIDTH}`} height={`${MASK_VIEW_HEIGHT}`} fill={colors.cards.onDark} mask="url(#maskReceipt)"/>
    </Svg>);
});
MaskView.displayName = 'MaskView';
/**
 * Though called Receipt, it is used to display info of buy/sell that is confirmed/pending/failed.
 */
const Receipt = ({ status = 'completed', title, titleExtra, subtitle, logoUrl, rows, isSwitch, alwaysShowStatus, width, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const icon = useMemo(() => {
        let ic = <StatusIcon width={18} height={18} state="in-progress"/>;
        if (status === 'cancelled' || status === 'failed') {
            ic = <StatusIcon state="cancelled-red" width={18} height={18}/>;
        }
        else if (status === 'completed' || status === 'partially_filled') {
            ic = <StatusIcon state="done" width={18} height={18}/>;
        }
        return ic;
    }, [status]);
    return (<View style={styles.container}>
      <View style={styles.fakeBackgroundReceipt}/>
      <View style={styles.headerContainer}>
        <Text Text-20 centered numberOfLines={2}>
          {title}
        </Text>
        {titleExtra != null && (<Text Text-14 Gray centered>
            {titleExtra}
          </Text>)}
        {subtitle != null && (<Text TextThin-14 Gray centered>
            {subtitle}
          </Text>)}
      </View>
      <View style={styles.logoContainer}>
        {isSwitch ? (<IconCircle width={64} height={64} color={colors.background.dark}>
            <IconCircle width={52} height={52} color={colors.background.brandLight}>
              <IconTransfer colorKey="brand"/>
            </IconCircle>
          </IconCircle>) : (<Logo R64 withOutline uri={logoUrl} resizeMode="contain"/>)}
        {icon != null && (<View style={styles.statusIconContainer}>
            <Squircle width={rem(24)} height={rem(24)} color={colors.borders.iconStroke}>
              {icon}
            </Squircle>
          </View>)}
      </View>

      <MaskView width={width}/>
      <View style={styles.bottomContainer}>
        {rows == null && (<View style={styles.indicatorContainer}>
            <Indicator dark/>
          </View>)}
        {rows != null && (status !== 'completed' || alwaysShowStatus) ? (<Row title={strings.statusTitle} info={strings.status[status]}/>) : null}
        {rows != null && <ReceiptRows {...rows}/>}
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingBottom: rem(16),
    },
    fakeBackgroundReceipt: {
        bottom: 0,
        position: 'absolute',
        left: rem(26),
        right: rem(26),
        height: rem(100),
        borderRadius: rem(20),
        backgroundColor: colors.cards.transparentOnDark,
    },
    bottomContainer: {
        width: '100%',
        paddingBottom: rem(12),
        backgroundColor: colors.cards.onDark,
        borderBottomLeftRadius: BORDER_RADIUS,
        borderBottomRightRadius: BORDER_RADIUS,
    },
    headerContainer: {
        marginTop: rem(32),
        paddingTop: rem(52),
        paddingHorizontal: rem(16),
        alignItems: 'center',
        gap: rem(4),
        paddingBottom: rem(11),
        backgroundColor: colors.cards.onDark,
        borderTopLeftRadius: BORDER_RADIUS,
        borderTopRightRadius: BORDER_RADIUS,
    },
    logoContainer: {
        top: 0,
        height: rem(64),
        aspectRatio: 1,
        position: 'absolute',
        alignSelf: 'center',
        alignItems: 'center',
    },
    receiptRow: {
        flexDirection: 'row',
        paddingVertical: rem(12),
        paddingHorizontal: PADDING,
        justifyContent: 'space-between',
    },
    indicatorContainer: {
        height: rem(314),
        alignItems: 'center',
        justifyContent: 'center',
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        width: rem(24),
        height: rem(24),
        position: 'absolute',
    },
}));
export default memo(Receipt);
